/**
 * /Recon portal — account.
 *
 * MVP: read-only profile (from /me), email-verification status,
 * password-change handoff to /portal/forgot, and a sign-out button.
 *
 * Future polish (post-Bucket-Q):
 *   - Login history (last 10 sign-ins from audit_log)
 *   - Active subscriptions (Continuous Watch billing status)
 *   - Stripe Customer Portal handoff for invoices
 *   - Identity-verification badge for PE clients
 *   - Linked Essentials / Vantage account management (Bucket Q')
 *
 * Profile edits aren't self-serve in this MVP. Updating contact_email
 * or display_name routes through the assessor (mailto) so audit_log
 * captures the request — same constraint as the bucket plan calls
 * out: client-name updates need an audit trail, not a quiet patch.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useClientAuth } from "@/auth/client-auth-context";
import "./account.css";

export function PortalAccount() {
  const { me, logout, accessToken } = useClientAuth();
  const [resendState, setResendState] = useState<
    "idle" | "sending" | "sent" | "cooldown" | "error"
  >("idle");
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  async function handleResendVerify() {
    setResendState("sending");
    setResendMessage(null);
    try {
      // Direct fetch — the generated API client doesn't yet include this
      // new endpoint (would require pnpm api:codegen against the live
      // OpenAPI schema). Same-origin POST with the bearer token.
      const response = await fetch("/api/v1/client-auth/resend-verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 429) {
        const retryAfter = response.headers.get("Retry-After");
        setResendState("cooldown");
        setResendMessage(
          retryAfter
            ? `Already sent recently — try again in ${Math.ceil(parseInt(retryAfter, 10) / 60)} minute(s).`
            : "Already sent recently — please wait a few minutes.",
        );
        return;
      }

      if (!response.ok) {
        setResendState("error");
        setResendMessage("Couldn't send the verification email. Try again in a moment.");
        return;
      }

      setResendState("sent");
      setResendMessage(
        `Sent to ${me?.contact_email}. Check your inbox (and spam folder). Link valid for 14 days.`,
      );
    } catch {
      setResendState("error");
      setResendMessage("Couldn't send the verification email. Try again in a moment.");
    }
  }

  if (!me) {
    return (
      <div className="account-page">
        <div className="account-inner">
          <p className="account-loading">Loading account…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-inner">
        <div className="account-header">
          <div className="eyebrow">Account</div>
          <h1 className="account-title">Account</h1>
          <p className="account-lede">
            Your profile and access settings. Reach out to your assessor
            to update contact details — changes flow through the engagement
            audit trail.
          </p>
        </div>

        {/* Profile */}
        <section className="account-section">
          <h2 className="account-section-title">Profile</h2>
          <div className="account-fields">
            <Field label="Display name" value={me.display_name} />
            <Field label="Contact name" value={me.contact_name} />
            <Field
              label="Email"
              value={me.contact_email || "—"}
              badge={
                me.email_verified
                  ? { tone: "verified", label: "Verified" }
                  : { tone: "pending", label: "Unverified" }
              }
            />
            <Field label="Company" value={me.company || "—"} />
          </div>
        </section>

        {/* Email verification — only visible when not verified */}
        {!me.email_verified && (
          <section className="account-section">
            <div className="account-callout account-callout-warn">
              <h3 className="account-callout-title">Verify your email</h3>
              <p className="account-callout-body">
                Commissioning is locked until your email is verified.
                Check your inbox for the verification link from /Recon
                — if you don't see it, click below and we'll send a
                fresh one (valid for 14 days).
              </p>
              <div className="account-callout-actions">
                <button
                  type="button"
                  className="recon-btn recon-btn-primary"
                  onClick={handleResendVerify}
                  disabled={resendState === "sending" || resendState === "cooldown"}
                >
                  {resendState === "sending" ? "Sending…" : "Resend verification email"}
                </button>
              </div>
              {resendMessage && (
                <p
                  className={
                    "account-callout-status " +
                    (resendState === "sent"
                      ? "account-callout-status-ok"
                      : "account-callout-status-warn")
                  }
                >
                  {resendMessage}
                </p>
              )}
            </div>
          </section>
        )}

        {/* Password */}
        <section className="account-section">
          <h2 className="account-section-title">Password</h2>
          <p className="account-section-body">
            Use the password reset flow to set a new password. We'll
            email a link to {me.contact_email || "the address on file"}.
          </p>
          <Link
            to="/portal/forgot"
            className="recon-btn recon-btn-secondary"
          >
            Change password
          </Link>
        </section>

        {/* Sign out */}
        <section className="account-section">
          <h2 className="account-section-title">Sign out</h2>
          <p className="account-section-body">
            End your session on this device. You'll need to sign back in
            to access engagements.
          </p>
          <button
            type="button"
            className="recon-btn recon-btn-secondary"
            onClick={() => {
              void logout();
            }}
          >
            Sign out
          </button>
        </section>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  badge,
}: {
  label: string;
  value: string;
  badge?: { tone: "verified" | "pending"; label: string };
}) {
  return (
    <div className="account-field">
      <div className="account-field-label">{label}</div>
      <div className="account-field-value-row">
        <div className="account-field-value">{value}</div>
        {badge && (
          <span className={`account-badge account-badge-${badge.tone}`}>
            {badge.label}
          </span>
        )}
      </div>
    </div>
  );
}
